/**
 * v2 Settings → API keys (admin only).
 *
 * Issue / list / revoke the keys that authenticate the external
 * management portal's calls to `/api/ext/*` (e.g. the GIG-ID subscriber
 * lookup). The plaintext key is shown ONCE at creation — only its
 * SHA-256 is stored. Non-admins get a 403 here (the nav link is also
 * hidden for them).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { api, asApiError } from "../../../api/client";
import { formatLocal } from "../../../lib/datetime";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";

type ApiKeyRow = {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_by_gigafy_username: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};
type ListResponse = { keys: ApiKeyRow[] };
type CreateResponse = ApiKeyRow & { key: string };

// Capabilities a key can grant. `subscriber` = the GIG-ID lookup;
// `diagnostics` = jobs/audit/transcripts + chassis port reads + MAC
// lookup + the refresh trigger (v0.31.0). Keep in sync with
// ALLOWED_API_KEY_SCOPES on the backend.
const SCOPE_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: "subscriber", label: "subscriber", hint: "GIG-ID subscriber lookup" },
  {
    value: "diagnostics",
    label: "diagnostics",
    hint: "jobs/audit/transcripts, chassis ports, MAC lookup, refresh trigger",
  },
];

function fmt(ts: string | null): string {
  return formatLocal(ts);
}

export function ApiKeys() {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["subscriber"]);
  // The freshly-minted plaintext key — shown once, then dismissed.
  const [newKey, setNewKey] = useState<CreateResponse | null>(null);

  const toggleScope = (value: string) =>
    setScopes((prev) =>
      prev.includes(value)
        ? prev.filter((s) => s !== value)
        : [...prev, value],
    );

  const q = useQuery<ListResponse>({
    queryKey: ["v2", "api-keys"],
    queryFn: async () => {
      const res = await api.get<ListResponse>("/keys");
      return res.data;
    },
    retry: false,
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<CreateResponse>("/keys", {
        name: name.trim(),
        scopes,
      });
      return res.data;
    },
    onSuccess: (d) => {
      setNewKey(d);
      setName("");
      setScopes(["subscriber"]);
      qc.invalidateQueries({ queryKey: ["v2", "api-keys"] });
      toast.push("API key created — copy it now", "success");
    },
    onError: (err) =>
      toast.push(asApiError(err).detail || "Failed to create key", "error"),
  });

  const revoke = useMutation({
    mutationFn: async (id: number) => api.delete(`/keys/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "api-keys"] });
      toast.push("Key revoked", "success");
    },
    onError: () => toast.push("Revoke failed", "error"),
  });

  // 403 → not an admin. The router gates on CHASSIS_API_KEY_ADMINS.
  const forbidden = asApiError(q.error).status === 403;

  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-100">
            <KeyRound className="h-4 w-4" />
            API keys
          </h1>
          <HelpButton topic="settings-api-keys" />
        </div>
        <p className="text-xs text-slate-500">
          Keys for the external lookup API. Use as{" "}
          <code className="font-mono">Authorization: Bearer &lt;key&gt;</code>{" "}
          against{" "}
          <code className="font-mono">
            GET /api/ext/subscriber/&#123;GIG-ID&#125;
          </code>
          . The key is shown once at creation; only its hash is stored. A key's{" "}
          <span className="font-mono">scopes</span> gate which endpoints it can
          reach — <span className="font-mono">subscriber</span> for the lookup,{" "}
          <span className="font-mono">diagnostics</span> for the read-only
          jobs/audit/transcripts endpoints.
        </p>
      </header>

      {forbidden ? (
        <p className="rounded border border-amber-800 bg-amber-900/30 p-3 text-xs text-amber-200">
          You're not authorised to manage API keys. Ask an admin (a user in
          the <code className="font-mono">CHASSIS_API_KEY_ADMINS</code>{" "}
          allowlist) to issue one.
        </p>
      ) : (
        <>
          {/* One-time key reveal */}
          {newKey && (
            <div className="rounded-lg border border-emerald-800 bg-emerald-950/40 p-3">
              <p className="mb-1 text-xs font-medium text-emerald-200">
                New key “{newKey.name}” — copy it now, it won't be shown again:
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 overflow-x-auto rounded bg-slate-950 px-2 py-1.5 font-mono text-xs text-emerald-100">
                  {newKey.key}
                </code>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(newKey.key);
                      toast.push("Copied", "success");
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-xs text-emerald-100 hover:bg-emerald-900/70"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => setNewKey(null)}
                  className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Create */}
          <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-900 shadow-border p-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-slate-400">New key name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. management-portal"
                className="w-64 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none"
              />
            </label>
            <div className="flex flex-col gap-1 text-xs">
              <span className="text-slate-400">Scopes</span>
              <div className="flex flex-wrap gap-3 py-1">
                {SCOPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    title={opt.hint}
                    className="inline-flex cursor-pointer items-center gap-1 text-slate-300"
                  >
                    <input
                      type="checkbox"
                      checked={scopes.includes(opt.value)}
                      onChange={() => toggleScope(opt.value)}
                    />
                    <span className="font-mono">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={
                create.isPending || name.trim() === "" || scopes.length === 0
              }
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-900/70 disabled:opacity-50"
            >
              <Plus className="h-3 w-3" />
              {create.isPending ? "Creating…" : "Create key"}
            </button>
          </div>

          {/* List */}
          <div className="rounded-lg bg-slate-900 shadow-border p-3">
            {q.isLoading ? (
              <p className="text-xs text-slate-500">Loading…</p>
            ) : (q.data?.keys.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-500">No keys yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1">Name</th>
                    <th className="px-2 py-1">Prefix</th>
                    <th className="px-2 py-1">Scopes</th>
                    <th className="px-2 py-1">Created by</th>
                    <th className="px-2 py-1">Created</th>
                    <th className="px-2 py-1">Last used</th>
                    <th className="px-2 py-1">Status</th>
                    <th className="px-2 py-1 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40 text-slate-300">
                  {(q.data?.keys ?? []).map((k) => (
                    <tr key={k.id}>
                      <td className="px-2 py-1.5">{k.name}</td>
                      <td className="px-2 py-1.5 font-mono">
                        <CopyableCell value={k.key_prefix} />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {(k.scopes ?? []).map((s) => (
                            <Badge
                              key={s}
                              tone={s === "diagnostics" ? "warning" : "neutral"}
                            >
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {k.created_by_gigafy_username}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                        {fmt(k.created_at)}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                        {fmt(k.last_used_at)}
                      </td>
                      <td className="px-2 py-1.5">
                        {k.revoked_at ? (
                          <Badge tone="muted">revoked</Badge>
                        ) : (
                          <Badge tone="success">active</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {!k.revoked_at && (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Revoke key "${k.name}"? Calls using it will start failing immediately.`,
                                )
                              ) {
                                revoke.mutate(k.id);
                              }
                            }}
                            disabled={revoke.isPending}
                            className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 disabled:opacity-50"
                          >
                            <Trash2 className="h-3 w-3" />
                            revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}
